import { CloseOutlined, HomeOutlined } from '@ant-design/icons';
import { message, Select } from 'antd';
import { Box, Button, FormGroup, Icon, Text } from 'components';
import { terminalListSelector } from 'features/merchants/redux';
import { FirebaseCollectionsType } from 'firebaseAPI';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useFirestore } from 'react-redux-firebase';
import { Firebase } from 'types/firebase';
/**
 * CONST
 */
/**
 * value should be lowercase
 * label should be uppercase
 */
const MODE_LIST = [
  { label: 'POS', value: 'pos' },
  { label: 'TERMINAL', value: 'terminal' },
  { label: 'APP_SWITCH', value: 'app_switch' },
];
/**
 * Types
 */
type TerminalMode = 'pos' | 'terminal' | 'app_switch';
export type ChangeTerminalModeProps = {
  merchantId: string;
  accountId: string;
  terminal: Firebase.Terminal;
  onSave: (
    inputName: string,
    newAcquirerMerchantId: string | number,
    forced: boolean,
  ) => void;
};
/**
 * ChangeTerminalMode
 */
const ChangeTerminalMode = (props: ChangeTerminalModeProps) => {
  /* Hooks */
  const firestore = useFirestore();
  /* Redux */
  const terminalList = useSelector(terminalListSelector(props?.accountId));
  const currentTerminal = terminalList[props?.terminal?.terminalId];
  /* Local state */
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [currentTerminalModeValue, setTerminalModeValue] = useState<
    TerminalMode | undefined
  >(undefined);
  const handleToggleEditing = () => {
    setIsEdit((isEditingPrev) => !isEditingPrev);
  };
  /* In-use vars */
  const currentMode = useMemo(() => {
    const currentModez = currentTerminal?.usingAppSwitch
      ? 'APP_SWITCH'
      : currentTerminal?.mode?.toLocaleUpperCase();
    return currentModez;
  }, [currentTerminal?.mode, currentTerminal?.usingAppSwitch]);
  /**
   * Reset edit value
   */
  const resetValue = () => {
    setTerminalModeValue(undefined);
  };
  /**
   * onDropdownHandleChange
   */
  const onDropdownHandleChange = (mode: TerminalMode) => {
    setTerminalModeValue(mode);
  };
  /**
   * onResetSuccess
   */
  const onResetSuccess = () => {
    handleToggleEditing();
    resetValue();
  };
  /**
   * Update terminal mode
   */
  const _onHandleChangeTerminalMode = async () => {
    try {
      if (currentTerminalModeValue === 'app_switch') {
        // update app switch and terminal mode; app switch can't be with pos mode
        await firestore
          .collection(FirebaseCollectionsType.TERMINALS)
          .doc(props.accountId)
          .collection(FirebaseCollectionsType.TERMINAL_LIST)
          .doc(props.terminal?.terminalId)
          .update({
            mode: 'terminal',
            usingAppSwitch: true,
          });
        onResetSuccess();
        message.success('Mode updated');
      } else {
        await firestore
          .collection(FirebaseCollectionsType.TERMINALS)
          .doc(props.accountId)
          .collection(FirebaseCollectionsType.TERMINAL_LIST)
          .doc(props.terminal?.terminalId)
          .update({
            mode: currentTerminalModeValue,
            // reset app switch always
            usingAppSwitch: false,
          });
        message.success('Mode updated');
        onResetSuccess();
      }
    } catch (e) {
      message.error('Failed to update mode');
    }
  };

  return (
    <Box py="24px">
      {!isEdit && (
        <Box>
          <Text size="ui3">Mode</Text>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Text size="ui2">{currentMode}</Text>
            <Button
              style={{ color: '#FF8A48' }}
              type="text"
              onClick={handleToggleEditing}
            >
              Change
            </Button>
          </Box>
        </Box>
      )}
      {isEdit && (
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
        >
          <Box display="flex" justifyContent="space-between" mb="8px">
            <Text size="ui3">Change mode</Text>
            <Box marginTop="-28px" mb="24px">
              <Button
                ghost
                onClick={() => {
                  handleToggleEditing();
                  resetValue();
                }}
              >
                <Icon icon={<CloseOutlined />} />
              </Button>
            </Box>
          </Box>
          <Box display="flex" justifyContent="space-between">
            <Select
              style={{ width: '100%', maxWidth: '300px' }}
              placeholder={'Choose mode'}
              // @ts-ignore
              onSelect={onDropdownHandleChange}
              value={currentTerminalModeValue?.toLocaleUpperCase()}
              defaultValue={currentMode?.toLocaleUpperCase() ?? undefined}
            >
              {MODE_LIST.map((opt) => {
                if (!opt?.value) {
                  return;
                }
                return (
                  <Select.Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Select.Option>
                );
              })}
            </Select>
            <Button type="primary" onClick={_onHandleChangeTerminalMode}>
              Save
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export { ChangeTerminalMode };
